import type { TemplateDto } from '@carbonwebmail/contracts'
import { Link } from 'react-router'
import { when } from '../../ui/format.js'
import { StatusPill } from '../../ui/StatusPill.js'

/**
 * One template, as something you can recognise on sight.
 *
 * A list of names is unusable the moment there are a dozen templates, because
 * the thing people remember about a template is what it *looks* like. So the
 * card leads with the design rather than with its name.
 *
 * **The thumbnail is the real markup, scaled.** Not a picture stored at save
 * time: that stops matching the template the first time somebody edits it, and
 * nothing would ever say so.
 *
 * Fully sandboxed, unlike `MessageFrame` — this is a still to recognise a
 * template by, and a link followed from a card nobody is reading would be a
 * click that went somewhere by accident.
 *
 * A prose template has no design to show, so it shows its opening words
 * instead. Faking a thumbnail for it would claim a layout it does not have.
 */

export function TemplateCard({
  template,
  busy,
  mayWrite,
  onDuplicate,
  onRemove,
}: {
  readonly template: TemplateDto
  readonly busy: boolean
  /**
   * Every action below writes something, so without it the card is a preview.
   *
   * "Use" starts a campaign rather than touching the template, and is gated
   * here all the same: no role in the table may write campaigns without also
   * being able to write templates, and a second flag threaded in for one link
   * would be a second thing to keep in step for no difference in behaviour.
   */
  readonly mayWrite: boolean
  readonly onDuplicate: () => void
  readonly onRemove: () => void
}) {
  return (
    <article className="flex flex-col overflow-hidden rounded-card border border-line bg-surface">
      <div className="relative h-[180px] overflow-hidden border-line border-b bg-surface-2">
        {template.body.kind === 'designed' ? (
          <iframe
            title={`${template.name} preview`}
            srcDoc={template.body.html}
            sandbox=""
            scrolling="no"
            // Rendered at full width and scaled down, so the thumbnail is the
            // design's real proportions rather than a squashed one. The scale
            // is a literal class: Tailwind generates classes by scanning source
            // text, so an assembled one does not exist in the stylesheet.
            className="h-[1600px] w-[700px] origin-top-left scale-[0.32] border-0 bg-white"
          />
        ) : (
          <p className="line-clamp-6 p-4 text-[12px] text-t2 leading-relaxed">
            {template.body.text}
          </p>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 flex-1 truncate font-semibold text-[14px] text-t1">
            {template.name}
          </h3>
          <StatusPill tone={template.body.kind === 'designed' ? 'ok' : 'neutral'}>
            {template.body.kind === 'designed' ? 'design' : 'plain text'}
          </StatusPill>
        </div>

        <p className="truncate text-[12px] text-t2">{template.subject}</p>
        <p className="text-[11px] text-t3">Changed {when(template.updatedAt)}</p>

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-3">
          {!mayWrite ? null : (
            <>
              {/*
            The first thing anybody wants from a template. A campaign takes a
            copy of it, which is why this leads somewhere new rather than
            opening the template itself.
          */}
              <Link
                className="rounded-pill bg-brand px-3.5 py-1.5 font-semibold text-[12px] text-brand-contrast"
                to={`/campaigns/new?template=${encodeURIComponent(template.templateId)}`}
              >
                Use
              </Link>
              {/* Editing a design is Compose. There is no second editor. */}
              <Link
                className="rounded-pill border border-line px-3.5 py-1.5 font-medium text-[12px] text-t2 hover:text-t1"
                to={`/compose/template/${template.templateId}`}
              >
                Edit
              </Link>
              <Action busy={busy} onClick={onDuplicate}>
                Duplicate
              </Action>
              <button
                type="button"
                disabled={busy}
                onClick={onRemove}
                className="ml-auto font-medium text-[12px] text-t3 hover:text-danger disabled:opacity-60"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  )
}

function Action({
  busy,
  onClick,
  children,
}: {
  readonly busy: boolean
  readonly onClick: () => void
  readonly children: string
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className="rounded-pill border border-line px-3.5 py-1.5 font-medium text-[12px] text-t2 hover:text-t1 disabled:opacity-60"
    >
      {children}
    </button>
  )
}
